    import React, { createContext, useState, useEffect, useContext } from 'react';
    import API_BASE_URL from "../Config/config";
    import axios from "axios";

    export const api = axios.create({
      baseURL: API_BASE_URL
    });


    const AuthContext = createContext();

    export function useAuth() {
      return useContext(AuthContext);
    }

    export function AuthProvider({ children }) {
      const [user, setUser] = useState(null);
      const [loading, setLoading] = useState(true);
      const [token, setToken] = useState(localStorage.getItem('token'));

      // Configure auth header for our API client
      useEffect(() => {
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
          delete api.defaults.headers.common['Authorization'];
        }
      }, [token]);

      // Fetch current user on mount
      useEffect(() => {
        async function fetchUser() {
          if (token) {
            try {
              const response = await api.get('/auth/user');
              setUser(response.data);
            } catch (error) {
              console.error('Failed to fetch user:', error);
              logout();
            }
          }
          setLoading(false);
        }
        fetchUser();
      }, [token]);

      async function login(email, password, expectedRole = null) {
        // Normalize input to prevent "valid" credentials failing due to whitespace/casing.
        const normalizedEmail = String(email).trim().toLowerCase();

        const response = await api.post('/auth/login', {
          email: normalizedEmail,
          password,
          expectedRole
        });

        const data = response.data;

        // One-time email OTP required — return the pending state to the caller.
        // Do NOT create a session yet; the real JWT is issued after OTP verification.
        if (data.requiresEmailOtp) {
          return { requiresEmailOtp: true, tempToken: data.tempToken, maskedEmail: data.maskedEmail };
        }

        const { token, user } = data;

        // Set header immediately to avoid any race with fetchUser() running on token change.
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setToken(token);
        setUser(user);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }

      async function sendOtp(phoneNumber, role) {
        const response = await api.post('/auth/send-otp', {
          phoneNumber,
          role
        });
        return response.data;
      }

      async function verifyOtp(phoneNumber, role, otp) {
        const response = await api.post('/auth/verify-otp', {
          phoneNumber,
          role,
          otp
        });

        const { token, user } = response.data;

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setToken(token);
        setUser(user);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }

      // ── One-time email OTP verification ───────────────────────
      // Called from VerifyEmailOtp page after user submits the code.
      async function verifyEmailOtp(tempToken, otp) {
        const response = await api.post('/auth/verify-email-otp', { tempToken, otp });
        const { token, user } = response.data;

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setToken(token);
        setUser(user);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }

      // Called from VerifyEmailOtp page when user clicks "Resend".
      async function resendEmailOtp(tempToken) {
        const response = await api.post('/auth/resend-email-otp', { tempToken });
        return response.data;
      }
      // ─────────────────────────────────────────────────────────

      async function register(userData) {
        const response = await api.post('/auth/register', userData);
        return response.data;
      }

      /**
       * googleLogin — send Google credential (ID token) to backend for verification.
       * Returns one of:
       *   { user, token }                        — successful login / new candidate
       *   { requiresEmailOtp, tempToken, ... }   — 2FA required
       *   { requiresRegistration, googleData }   — new recruiter, must complete onboarding
       */
      async function googleLogin(credential, role) {
        const response = await api.post('/auth/google', { credential, role });
        const data = response.data;

        // 2FA required (same flow as password login)
        if (data.requiresEmailOtp) {
          return { requiresEmailOtp: true, tempToken: data.tempToken, maskedEmail: data.maskedEmail };
        }

        // New recruiter — needs to complete company registration
        if (data.requiresRegistration) {
          return { requiresRegistration: true, googleData: data.googleData };
        }

        // Successful login or new candidate created
        const { token, user } = data;
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setToken(token);
        setUser(user);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }

      function logout() {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        delete axios.defaults.headers.common['Authorization'];
      }

      // The 'value' object makes these variables/functions available to other components
      const value = {
        user,
        setUser, // Added this so Profile.js can update the user state
        login,
        googleLogin,
        sendOtp,
        verifyOtp,
        verifyEmailOtp,
        resendEmailOtp,
        register,
        logout,
        loading,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
        isSuperAdmin: user?.role === 'super_admin',
      };

      return (
          <AuthContext.Provider value={value}>
            {!loading && children}
          </AuthContext.Provider>
      );
    }
